import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';
import { cn } from '@/lib/utils';

const TEMPLATES = [
  {
    id: 'legal',
    title: 'Legal Instruments',
    description: 'Precision-engineered contracts, NDAs, and formal agreements with ironclad terminology.',
    icon: 'gavel',
    badge: 'Formal',
    color: 'bg-secondary-container text-on-secondary-container',
    snippet: 'This Non-Disclosure Agreement (the "Agreement") is entered into as of...'
  },
  {
    id: 'academic',
    title: 'Scholarly Articles',
    description: 'High-density prose structures for research summaries, essays, and academic treatises.',
    icon: 'school',
    badge: 'Research',
    color: 'bg-tertiary-container text-on-tertiary-container',
    snippet: 'Abstract: This paper explores the intersection of artificial intelligence and...'
  },
  {
    id: 'creative',
    title: 'Creative Manuscripts',
    description: 'Narrative foundations for screenplays, short stories, and poetic experiments.',
    icon: 'auto_stories',
    badge: 'Draft',
    color: 'bg-primary-container text-on-primary-container',
    snippet: 'The storm broke over the spire just as the bells began to toll...'
  },
  {
    id: 'business',
    title: 'Executive Briefs',
    description: 'Concise, high-impact structures for strategy documents and executive summaries.',
    icon: 'business_center',
    badge: 'Corporate',
    color: 'bg-surface-container-highest text-on-surface-variant',
    snippet: 'Executive Summary: The following brief outlines the strategic objectives for...'
  }
];

export function TemplatesPage() {
  const navigate = useNavigate();

  const handleSelect = (template: typeof TEMPLATES[0]) => {
    navigate('/', { 
      state: { 
        snippet: template.snippet, 
        title: template.title,
        prompt: `Create a ${template.title.toLowerCase()} about...`
      } 
    });
  };

  return (
    <AppLayout>
      <div className="flex-1 bg-surface-container-low overflow-y-auto">
        <header className="bg-surface border-b border-outline-variant px-4 md:px-margin-page py-12">
          <div className="max-w-max-width-doc mx-auto">
             <span className="font-label-caps text-label-caps text-primary uppercase tracking-widest mb-2 block">Foundations</span>
             <h1 className="font-h1 text-h1 text-on-surface mb-4">Manuscript Templates</h1>
             <p className="font-body-lg text-body-lg text-on-surface-variant max-w-2xl">
               Select a structural foundation to begin your work. Each template is calibrated for specific linguistic density and stylistic requirements.
             </p>
          </div>
        </header>

        <main className="px-4 md:px-margin-page py-12">
          <div className="max-w-max-width-doc mx-auto grid grid-cols-1 md:grid-cols-2 gap-gutter">
            {TEMPLATES.map((template) => (
              <div 
                key={template.id}
                onClick={() => handleSelect(template)}
                className="group cursor-pointer bg-surface border border-outline-variant rounded-xl p-8 flex flex-col gap-6 transition-all hover:shadow-xl hover:-translate-y-1 active:translate-y-0 active:shadow-md"
              >
                <div className="flex justify-between items-start">
                  <div className={cn("w-16 h-16 rounded-xl flex items-center justify-center paper-stack shadow-outline-variant/20 transition-transform group-hover:scale-110", template.color)}>
                    <span className="material-symbols-outlined text-[32px]">{template.icon}</span>
                  </div>
                  <span className={cn("px-3 py-1 rounded-full font-label-caps text-[10px] uppercase tracking-wider border border-outline-variant/30", template.color)}>
                    {template.badge}
                  </span>
                </div>

                <div>
                  <h3 className="font-h3 text-h3 text-on-surface mb-2">{template.title}</h3>
                  <p className="font-body-md text-body-md text-on-surface-variant leading-relaxed">
                    {template.description}
                  </p>
                </div>

                <div className="mt-auto flex items-center gap-2 text-primary font-label-caps text-[11px] uppercase tracking-widest pt-4 border-t border-outline-variant/10">
                  <span>Initialize Template</span>
                  <span className="material-symbols-outlined text-[18px] transition-transform group-hover:translate-x-1">arrow_forward</span>
                </div>
              </div>
            ))}
          </div>

          <div className="max-w-max-width-doc mx-auto mt-20 p-12 bg-surface-container-highest rounded-2xl border border-dashed border-outline-variant text-center">
             <span className="material-symbols-outlined text-[48px] text-outline mb-4">add_circle</span>
             <h2 className="font-h2 text-h2 text-on-surface mb-2">Custom Architectures</h2>
             <p className="font-body-md text-body-md text-on-surface-variant mb-6">
               Need a specialized structure not listed here? Our AI scribes can forge entirely new architectural patterns based on your requirements.
             </p>
             <button 
               onClick={() => navigate('/')}
               className="bg-primary text-on-primary px-8 py-3 rounded-DEFAULT font-label-caps text-label-caps uppercase tracking-widest hover:bg-primary/90 transition-all shadow-md active:translate-y-px"
             >
               Define Custom Draft
             </button>
          </div>
        </main>
      </div>
    </AppLayout>
  );
}